import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';

import {
  getRoleChangedState,
  listenForRoleChanges,
  markRoleChanged,
  subscribeRoleChanged,
  type RoleChangedState,
} from '@/auth/roleChange';
import { fetchAssignments, getAssignmentsSnapshot } from '@/auth/roleSelection';

/** "Your role changed to Activation Manager – Adidas." (roleName: the assignment label) */
export const roleChangedMessage = (
  state: Pick<RoleChangedState, 'roleName' | 'contextName'>,
): string => {
  if (!state.roleName) return 'Your role changed in another tab or window. Reload to continue.';
  const context = state.contextName ? ` (${state.contextName})` : '';
  return `Your role changed to ${state.roleName}${context}. Reload to continue.`;
};

/**
 * Blocking banner of a tab whose role changed elsewhere (Authorization-Plan
 * §13.6): shown on the 'pnet-role' broadcast / storage signal or on a 409
 * `role_changed`. Covers the page, so nothing else can be clicked; writes are
 * also refused in roleAwareFetch(). Reload is the only way on.
 *
 * TODO(§13.6): unsaved form input is lost on reload; keep it per role.
 */
export const RoleChangedBanner = () => {
  const [state, setState] = useState<RoleChangedState>(getRoleChangedState());

  useEffect(() => listenForRoleChanges(), []);
  useEffect(() => subscribeRoleChanged(setState), []);

  // A 409 only carries the id: look the name up once.
  useEffect(() => {
    if (!state.blocked || state.roleName || state.assignmentId === null) return;
    const known = getAssignmentsSnapshot().find((a) => a.id === state.assignmentId);
    if (known) {
      markRoleChanged({ roleName: known.label || null, contextName: null });
      return;
    }
    let cancelled = false;
    void fetchAssignments().then((outcome) => {
      if (cancelled || !outcome.ok) return;
      const found = outcome.list.assignments.find((a) => a.id === state.assignmentId);
      if (found) markRoleChanged({ roleName: found.label || null, contextName: null });
    });
    return () => {
      cancelled = true;
    };
  }, [state.blocked, state.roleName, state.assignmentId]);

  if (!state.blocked) return null;

  // The app's standard dialog: it cannot be dismissed (no onClose, no
  // backdrop click, no Escape); Reload is the only way on.
  return (
    <Dialog
      open
      role='alertdialog'
      aria-labelledby='role-changed-title'
      aria-describedby='role-changed-message'
      data-testid='role-changed-banner'
      maxWidth='xs'
      fullWidth
      disableEscapeKeyDown
      sx={{ zIndex: 3000 }}
    >
      <DialogTitle id='role-changed-title' sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SyncProblemIcon color='warning' />
        Role changed
      </DialogTitle>
      <DialogContent>
        <Box>
          <Typography id='role-changed-message' variant='body2'>
            {roleChangedMessage(state)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant='contained' color='primary' onClick={() => window.location.reload()}>
          Reload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleChangedBanner;
